import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OrganizationMemberV2, OrganizationRoleV2 } from '../../../types/organizationTeam';
import { TableActionButton } from '../components/cells';
import { teamErrorText } from './teamErrorText';

export const memberName = (m: OrganizationMemberV2) =>
  [m.firstName, m.lastName].filter(Boolean).join(' ') || m.phone || m.email || m.id;

interface TeamMembersPanelProps {
  members: OrganizationMemberV2[];
  roles: OrganizationRoleV2[];
  onChangeRole: (userId: string, roleId: string) => Promise<unknown>;
  onRemove: (member: OrganizationMemberV2) => void;
  isSaving: boolean;
}

export const TeamMembersPanel = ({ members, roles, onChangeRole, onRemove, isSaving }: TeamMembersPanelProps) => {
  const { t } = useTranslation();
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  // Controlled by the server value: a failed change leaves m.role unchanged,
  // so the select snaps back on its own.
  const change = async (m: OrganizationMemberV2, roleId: string) => {
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[m.id];
      return next;
    });
    try {
      await onChangeRole(m.id, roleId);
    } catch (err) {
      setRowErrors((prev) => ({ ...prev, [m.id]: teamErrorText(err, t) }));
    }
  };

  if (members.length === 0) {
    return <p className="team-muted team-panel-empty">{t('dashboard.team.members.empty')}</p>;
  }

  return (
    <table className="team-members-table">
      <thead>
        <tr>
          <th>{t('dashboard.team.members.columnMember')}</th>
          <th>{t('dashboard.team.members.columnRole')}</th>
          <th aria-hidden="true" />
        </tr>
      </thead>
      <tbody>
        {members.map((m) => (
          <tr key={m.id}>
            <td>
              <div className="team-member-name">{memberName(m)}</div>
              <div className="team-muted">{[m.phone, m.email].filter(Boolean).join(', ')}</div>
              {rowErrors[m.id] && <div className="role-form-error" role="alert">{rowErrors[m.id]}</div>}
            </td>
            <td>
              <select
                className="team-role-select"
                value={m.role?.id ?? ''}
                onChange={(e) => void change(m, e.target.value)}
                aria-label={t('dashboard.team.members.changeRoleAria', { name: memberName(m) })}
                disabled={isSaving}
              >
                {!m.role && <option value="">—</option>}
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </td>
            <td className="team-member-actions">
              <TableActionButton
                icon="person_remove"
                onClick={() => onRemove(m)}
                ariaLabel={t('dashboard.team.members.removeAria', { name: memberName(m) })}
                title={t('dashboard.team.members.remove')}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
